/**
 * Spending and usage panel — spend limits per provider, and use today and
 * this week from usage records.
 *
 * Reads (each time-limited, each isolated to this panel):
 *  - The costs read (one engine request): per-provider usage and the summary.
 *  - The combined provider-settings read (one engine request, shared with
 *    Keys and accounts): per-provider spend caps. Until the live engine
 *    serves it, the caps block is honest "Coming soon" and usage is
 *    unaffected. Caps render read-only here; editing returns with the next
 *    engine update rather than reviving the per-provider read fan-out.
 */

import React from 'react';
import {
  getLLMCostSummary,
  getProviderSettings,
  type LLMCostSummaryResponse,
} from '@/api/llmSettings';
import { ComingSoonBlock, RouterReadError, RouterReadLoading } from './PanelErrorBoundary';
import { useRouterRead } from './useRouterRead';
import { PROVIDER_SETTINGS_SWR_KEY } from './KeysAndAccountsPanel';

const COSTS_SWR_KEY = '/api/v1/settings/llm/costs?days=30';

function formatUsd(value: number | null | undefined): string {
  if (value == null) return '—';
  return `$${value.toFixed(2)}`;
}

function formatCap(value: string | null): string {
  return value == null ? 'Unlimited' : `$${value}`;
}

export const SpendingAndUsagePanel: React.FC = () => {
  const costs = useRouterRead<LLMCostSummaryResponse>(COSTS_SWR_KEY, (signal) =>
    getLLMCostSummary(30, { signal }),
  );
  const settings = useRouterRead(PROVIDER_SETTINGS_SWR_KEY, (signal) => getProviderSettings({ signal }));

  if (costs.error) {
    return <RouterReadError error={costs.error} onRetry={costs.retry} />;
  }
  if (costs.isLoading) {
    return <RouterReadLoading label="Loading spending and usage…" />;
  }

  const spend = costs.data?.provider_spend ?? [];
  const summary = costs.data?.summary;
  const callModes = costs.data?.call_modes;
  const settingsFailed = Boolean(settings.error);
  const capEntries = Object.entries(settings.data?.providers ?? {});

  return (
    <div data-testid="models-routing-spending-panel" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-space-3)' }}>
      {summary ? (
        <div style={{ display: 'flex', gap: 'var(--sp-space-3)', flexWrap: 'wrap' }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
            Last {summary.days} days: <span style={{ color: 'var(--sp-text)' }}>{formatUsd(summary.total_cost_usd)}</span>
          </div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
            Projected month: <span style={{ color: 'var(--sp-text)' }}>{formatUsd(costs.data?.projected_monthly_cost_usd)}</span>
          </div>
          {callModes ? (
            <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)' }}>
              Calls — subscription {callModes.subscription_proxy_calls} · own-key {callModes.byok_calls} · embedded {callModes.embedded_calls}
            </div>
          ) : null}
        </div>
      ) : null}

      <div>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--sp-text-dim)', textTransform: 'uppercase', marginBottom: 6 }}>
          Usage per provider
        </div>
        {spend.length === 0 ? (
          <div className="sp-empty" style={{ padding: '24px 12px' }}>
            No usage recorded in this window.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {spend.map((item) => (
              <div
                key={item.provider_id}
                data-testid={`mr-spend-row-${item.provider_id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
                  alignItems: 'center',
                  gap: 'var(--sp-space-3)',
                  border: '1px solid var(--sp-border)',
                  borderRadius: 'var(--sp-radius-lg)',
                  background: 'var(--sp-bg-elev)',
                  padding: '10px var(--sp-space-3)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-text)' }}>{item.display_name}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                    {item.provider_id} · {item.month_call_count} calls this month
                  </div>
                </div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)', textAlign: 'right' }}>
                  today {formatUsd(item.today_cost_usd)}
                </div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)', textAlign: 'right' }}>
                  week {formatUsd(item.week_cost_usd)}
                </div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)', textAlign: 'right' }}>
                  month {formatUsd(item.month_cost_usd)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', color: 'var(--sp-text-dim)', textTransform: 'uppercase', marginBottom: 6 }}>
          Spend limits per provider
        </div>
        {settingsFailed ? (
          <ComingSoonBlock
            title="Spend limits per provider"
            body="The combined read that carries every provider's daily and monthly cap in one request is on engine main and arrives with the next engine update. Usage above keeps working without it."
            testId="mr-caps-coming-soon"
          />
        ) : capEntries.length === 0 ? (
          <div className="sp-empty" style={{ padding: '24px 12px' }}>
            No provider limits reported.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {capEntries.map(([providerId, entry]) =>
                entry.status === 'available' ? (
                  <div
                    key={providerId}
                    data-testid={`mr-cap-row-${providerId}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
                      alignItems: 'center',
                      gap: 'var(--sp-space-3)',
                      border: '1px solid var(--sp-border)',
                      borderRadius: 'var(--sp-radius-lg)',
                      background: 'var(--sp-bg-elev)',
                      padding: '10px var(--sp-space-3)',
                    }}
                  >
                    <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text)' }}>{providerId}</div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)', textAlign: 'right' }}>
                      daily {formatCap(entry.spend_cap.daily_limit_usd)}
                    </div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-muted)', textAlign: 'right' }}>
                      monthly {formatCap(entry.spend_cap.monthly_limit_usd)}
                    </div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--sp-text-dim)', textAlign: 'right' }}>
                      warn {entry.spend_cap.warning_percent == null ? '—' : `${entry.spend_cap.warning_percent}%`}
                    </div>
                  </div>
                ) : (
                  <div
                    key={providerId}
                    data-testid={`mr-cap-row-${providerId}`}
                    style={{
                      border: '1px solid var(--sp-border)',
                      borderRadius: 'var(--sp-radius-lg)',
                      padding: '10px var(--sp-space-3)',
                      fontSize: 11,
                      color: 'var(--sp-text-dim)',
                    }}
                  >
                    {providerId} — limits unavailable ({entry.reason})
                  </div>
                ),
              )}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--sp-text-dim)' }}>
              Limits are read-only on this surface; editing returns with the next engine update.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
